import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";

import { VNode } from "src/interfaces/interfaces";
import PublishAppViewModel, {
  AGOL_USER_ROOT_FOLDER_KEY,
  RedirectOption,
  UserFolder,
} from "../../data/PublishApp/PublishAppViewModel";
import { navbarRoute } from "../../ui/NavBar/NavBarViewModel";
import { AppTypeInfo, EResourceType, ResourceInfo } from "../AppTypeTile/AppTypeTile";

import AppT9N from "../../../t9n/App/App.json";

import "./styles/PublishForm.scss";
import { applyShadowStyle, getBaseUrl } from "../../utils/utils";
import {
  CHIP_OVERFLOW_FIX,
  SHADOW_STYLE_COMBOBOX,
} from "../../utils/shadowStyles";
import { logInstantAppPublish } from "../../utils/telemetryUtils";

/**
 * Different UIs the PublishForm can be launched as
 * "positionedOverlay" - implies that this is being rendered in a Preview Modal - ** important for telemetry - if this changes, consider necessary updates to telemetry
 */
export type PublishUI = "modal" | "positionedOverlay";
/** Different Modes the PublishForm can use to create apps */
export type PublishMode = "newApp" | "copyApp" | "fromExistingApp";

export interface IPublishFormProperties extends __esri.WidgetProperties {
  ui: PublishUI;
  mode: PublishMode;
  appTypeInfo: AppTypeInfo;

  /** Required when mode==="newApp" */
  resourceInfo?: ResourceInfo;
  /** Required when mode==="copyApp" || mode ==="fromExistingApp" */
  existingAppItem?: __esri.PortalItem;

  publishAppVM: PublishAppViewModel;

  portal?: __esri.Portal;

  updateFunction?: Function;
  feedbackFunction?: (msg: string, success: boolean) => void;
  sendFocusOnClose: Function;
}

const FORBIDDEN_TITLE_CHARS = ["<", ">"];
const TITLE_AND_TAG_STR_MAX_LENGTH = 256;
const AMOUNT_OF_TAGS_MAX = 128;

@subclass("Landing.widgets.PublishForm")
export default class PublishForm extends Widget {
  @property()
  ui: PublishUI = "modal";

  @property()
  mode: PublishMode;

  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  currentNavRoute: navbarRoute;

  @property()
  resourceInfo: ResourceInfo;

  @property()
  existingAppItem: __esri.PortalItem;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  updateFunction?: Function;

  @property()
  feedbackFunction?: (msg: string, success: boolean, appId?: string) => void;

  @property()
  sendFocusOnClose: Function;

  @property()
  publishResultMessage: string;

  @property()
  publishResultMessageType: "success" | "error";

  @property()
  _appTitle: string;
  @property()
  _appTags: string[] = [];
  @property()
  _folderId: string = AGOL_USER_ROOT_FOLDER_KEY;

  @property()
  _isFormValid: boolean = false;
  @property()
  isTitleValid: boolean = null;
  /** Tags and Folder is always valid */
  @property()
  _isTagsValid: boolean = true;
  @property()
  _isFolderValid: boolean = true;

  @property()
  _titleInvalidMsg: string;
  @property()
  _tagsInvalidMsg: string;

  @property()
  _combobox: HTMLCalciteComboboxElement;
  @property()
  _currentExtraVal: string;
  @property()
  _comboboxGrandChildren: HTMLCollection;

  @property()
  portal: __esri.Portal;

  @property()
  startingTags: VNode[] = [];

  @property()
  userTags: VNode[] = [];

  private _userTags: any;

  constructor(properties?: IPublishFormProperties) {
    super(properties);
  }

  postInitialize() {
    this._folderId = this._decideDefaultSelectedFolder();
    this.getUserTags();

    this.startingTags =
      this.mode === "newApp" || this.mode === "fromExistingApp"
        ? this._renderTags(this?.resourceInfo?.resourceItem?.tags, true)
        : this._renderTags(this?.existingAppItem?.tags, true);
  }

  getUserTags(): any {
    // fetch user tags from https://www.arcgis.com/sharing/rest/community/users/jsmith/tags
    // and return them in the format of an array of strings
    fetch(
      `${getBaseUrl(this.portal)}/sharing/rest/community/users/${
        this.portal.user.username
      }/tags?f=json&token=${(this.portal as any).credential.token}`
    )
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        const tags = data.tags.map((tag) => {
          return tag.tag;
        });
        this._userTags = tags;
        this.userTags = this._renderTags(tags, false);
        return data;
      })
      .catch((err) => {
        console.error(err);
        return err;
      });
  }

  render(): VNode {
    const form = this._renderForm();
    return this.ui === "positionedOverlay"
      ? this._renderPositionedOverlay(form)
      : this._renderModal(form);
  }

  private _renderForm(): VNode {
    return (
      <div class="publish-form__form">
        {this._renderAppTitleInput()}
        {this._renderTagsInput()}
        {this._renderFolderInput()}
      </div>
    );
  }

  private _renderPositionedOverlay(form: VNode) {
    return (
      <div class="publish-form">
        <focus-trap>
          {this._renderHeader()}
          {form}
          {this._renderOverlayBtns()}
        </focus-trap>
      </div>
    );
  }

  private _renderHeader(): VNode {
    return (
      <div class="publish-form__header">
        <h3>
          {this.appMessages.globalNav.title} - {this.appTypeInfo.appTypeTitle}
        </h3>

        <calcite-button
          icon-start="x"
          scale="l"
          kind="neutral"
          appearance="transparent"
          onclick={(e) => {
            e.preventDefault();
            this._destroyPublishOverlay();
          }}
        ></calcite-button>
      </div>
    );
  }

  private _renderOverlayBtns(): VNode[] {
    const { publishForm, globalNav } = this.appMessages;

    let primaryBtnClickFunc: Function;
    let secondaryBtnClickFunc: Function;
    let primaryBtnLabel: string = globalNav.title;
    let secondaryBtnLabel: string = publishForm.publish;

    let showSettingsUpdateWarning: boolean = false;

    if (this.mode === "newApp") {
      primaryBtnClickFunc = this._publishNewApp.bind(this, "goToConfigure");
      secondaryBtnClickFunc = this._publishNewApp.bind(this, "goToItemDetails");
    } else if (this.mode === "copyApp") {
      // No need for copyApp in the IFramePreviewModal
    } else if (this.mode === "fromExistingApp") {
      primaryBtnClickFunc = this._publishNewAppFromExisting.bind(
        this,
        "goToConfigure"
      );
      secondaryBtnClickFunc = this._publishNewAppFromExisting.bind(
        this,
        "goToItemDetails"
      );
      showSettingsUpdateWarning = true;
    }
    return [
      showSettingsUpdateWarning ? (
        <div>{publishForm.settingsUpdateWarning}</div>
      ) : null,
      <div class="publish-form__button">
        {/* Commenting out "Publish" button for 9.3 release, leaving code in because we might reintroduce later */}
        {/* <div>
          <calcite-button
            width="full"
            appearance="outline"
            onclick={secondaryBtnClickFunc}
            disabled={!this._isFormValid || this.publishAppVM.isPublishing}
            loading={this.publishAppVM.isPublishing && this.publishAppVM.redirectionTarget === "goToItemDetails"}
          >
            {secondaryBtnLabel}
          </calcite-button>
        </div> */}
        <div>
          <calcite-button
            width="full"
            scale="l"
            onclick={primaryBtnClickFunc}
            disabled={!this._isFormValid || this.publishAppVM.isPublishing}
            loading={
              this.publishAppVM.isPublishing &&
              this.publishAppVM.redirectionTarget === "goToConfigure"
            }
          >
            {primaryBtnLabel}
          </calcite-button>
        </div>
      </div>,
    ];
  }

  private _renderModal(form: VNode): VNode {
    const { publishForm, modals, globalNav } = this.appMessages;
    let primaryBtnClickFunc: Function;
    let primaryBtnLabel: string;
    let showSettingsUpdateWarning: boolean = false;

    if (this.mode === "newApp") {
      primaryBtnClickFunc = this._publishNewApp.bind(this, "goToConfigure");
      primaryBtnLabel = globalNav.title;
    } else if (this.mode === "copyApp") {
      primaryBtnClickFunc = this._copyApp.bind(this);
      primaryBtnLabel = publishForm.copyApp;
    } else if (this.mode === "fromExistingApp") {
      primaryBtnClickFunc = this._publishNewAppFromExisting.bind(
        this,
        "goToConfigure"
      );
      primaryBtnLabel = globalNav.title;
      showSettingsUpdateWarning = true;
    }
    return (
      <calcite-modal
        class="publish-form-modal"
        open={true}
        afterCreate={(modal: HTMLCalciteModalElement) => {
          this._handleModalClose(modal);
          modal.setFocus();
        }}
        messageOverrides={{ close: modals.closeButtonTip }}
      >
        <div slot="header">
          {globalNav.title} - {this.appTypeInfo.appTypeTitle}
        </div>
        <div slot="content">{form}</div>
        {showSettingsUpdateWarning && (
          <div slot="back" class="settings-update-warning">
            <p>{publishForm.settingsUpdateWarning}</p>
          </div>
        )}
        <calcite-button
          slot="secondary"
          width="full"
          scale="l"
          appearance="outline"
          onclick={() => {
            this._destroyPublishOverlay();
          }}
        >
          {publishForm.close}
        </calcite-button>
        <calcite-button
          onclick={primaryBtnClickFunc}
          slot="primary"
          scale="l"
          disabled={!this._isFormValid || this.publishAppVM.isPublishing}
          loading={this.publishAppVM.isPublishing}
          width="full"
        >
          {primaryBtnLabel}
        </calcite-button>
      </calcite-modal>
    );
  }

  private _handleModalClose(modal: HTMLCalciteModalElement): void {
    modal.addEventListener("calciteModalClose", () => {
      this._destroyPublishOverlay();
    });
  }

  private _renderAppTitleInput(): VNode {
    const { title, titlePlaceHolder } = this.appMessages.publishForm;

    return (
      <calcite-label>
        {title}
        <calcite-input
          scale="l"
          required={true}
          status={this.isTitleValid === false ? "invalid" : "valid"}
          placeholder={titlePlaceHolder}
          max-length={`${TITLE_AND_TAG_STR_MAX_LENGTH}`}
          clearable="true"
          value={this._decideTitleDefaultValue()}
          afterCreate={(input: HTMLCalciteInputElement) => {
            input.addEventListener(
              "calciteInputInput",
              this._handleAppTitleWithDebounce.bind(this)
            );
            input.addEventListener(
              "click",
              this._handleAppTitleValidation.bind(this)
            );
            if (input.value != null && input.value !== "") {
              this._handleAppTitleValidation({
                target: { value: input.value },
              });
            }
          }}
        ></calcite-input>
        {this.isTitleValid === false ? (
          <calcite-input-message
            active={true}
            status="invalid"
            icon="exclamation-mark-triangle"
          >
            {this._titleInvalidMsg}
          </calcite-input-message>
        ) : null}
      </calcite-label>
    );
  }

  private _publishNewApp(redirection: RedirectOption): void {
    this.publishAppVM
      .createApp(
        {
          redirect: redirection,
          title: this._appTitle,
          folderId: this._folderId,
          tags: this._formatAGOLTags(),
          resourceInfo: this.resourceInfo,
          appTypeInfo: this.appTypeInfo,
        },
        this._publishTelemetryCall.bind(this)
      )
      .then((resultMsg: [string, string]) => {
        this?.feedbackFunction(resultMsg[0], true, resultMsg[1]);
        this._destroyPublishOverlay();
      })
      .catch((err) => {
        this?.feedbackFunction(err, false);
        this._destroyPublishOverlay();
        console.error("Publish err", err);
      });
  }

  private _copyApp(): void {
    const destroyFunc = this._destroyPublishOverlay.bind(this);
    this.publishAppVM
      .copyApp(
        {
          newTitle: this._appTitle,
          newTags: this._formatAGOLTags(),
          folder: this._folderId,
          appToCopyItem: this.existingAppItem,
          appTypeInfo: this.appTypeInfo,
          isOwner: this.existingAppItem.owner === this.portal.user.username,
        },
        this._publishTelemetryCall.bind(this)
      )
      .then((resultMsg: [string, string]) => {
        this?.feedbackFunction(resultMsg[0], true, resultMsg[1]);
        destroyFunc();
      })
      .catch((err) => {
        this?.feedbackFunction(err, false);
        destroyFunc();
        console.error("copy err", err);
      });
  }

  private async _publishNewAppFromExisting(
    redirection: RedirectOption
  ): Promise<void> {
    this.publishAppVM
      .createAppFromExisting(
        {
          redirect: redirection,
          title: this._appTitle,
          folderId: this._folderId,
          tags: this._formatAGOLTags(),
          resourceInfo: this.resourceInfo,
          appTypeInfo: this.appTypeInfo,
        },
        this.existingAppItem.id,
        this._publishTelemetryCall.bind(this)
      )
      .then((resultMsg: any) => {
        this?.feedbackFunction(resultMsg[0], true);
        this._destroyPublishOverlay();
      })
      .catch((err) => {
        this?.feedbackFunction(err, false);
        this._destroyPublishOverlay();
        console.error("Publish err", err);
      });
  }

  private _publishTelemetryCall() {
    logInstantAppPublish(
      this.publishAppVM.telemetry,
      this.publishAppVM.navbarVM.navigationRoute,
      this.appTypeInfo.appTypeUrlFragment,
      this.mode,
      this.ui
    );
  }

  private _determineFormValidity(): void {
    this._isFormValid =
      this.isTitleValid && this._isTagsValid && this._isFolderValid;
  }

  private _debounceHelper: string;
  /** Debounces the times that validation is called when typing in title input */
  private _handleAppTitleWithDebounce(event) {
    const strToValidate = event?.target?.value?.trim();
    this._debounceHelper = strToValidate;
    setTimeout(() => {
      if (this._debounceHelper === strToValidate || strToValidate == false) {
        this._handleAppTitleValidation(event);
      }
    }, 100);
  }

  @property()
  _currentValue = -1;

  private async _handleAppTitleValidation(event): Promise<void> {
    this._currentValue += 1;
    const localValue = this._currentValue;

    const titleVal: string = event?.target?.value?.trim() ?? "";
    this._appTitle = titleVal;

    if (titleVal === "") {
      if (localValue === this._currentValue) this._showInvalidEmptyTitle();
    } else if (this._hasForbiddenChars(titleVal)) {
      if (localValue === this._currentValue) this._showInvalidTitleChars();
    } else if (
      await this.publishAppVM.doesTitleExist(titleVal, this._folderId)
    ) {
      if (localValue === this._currentValue)
        this._showInvalidTitleAlreadyExists(titleVal);
    } else {
      if (localValue === this._currentValue) this._showValidTitle();
    }
  }

  private _decideTitleDefaultValue(): string {
    const { copyFormKeyword } = this.appMessages.publishForm;
    if (this.mode === "copyApp") {
      return `${this.existingAppItem.title} - ${copyFormKeyword}`;
    } else {
      return this?.resourceInfo?.resourceItem != null
        ? this.resourceInfo.resourceItem.title
        : null;
    }
  }

  private _renderTagsInput() {
    const { publishForm } = this.appMessages;
    return (
      <calcite-label>
        {publishForm.tags}
        <calcite-combobox
          label={publishForm.tags}
          placeholder={publishForm.tags}
          selection-mode="multiple"
          scale="l"
          overlay-positioning="fixed"
          allow-custom-values="true"
          max-items="0"
          afterCreate={(combobox: HTMLCalciteComboboxElement) => {
            this._comboboxHandler(combobox);
            applyShadowStyle(SHADOW_STYLE_COMBOBOX, combobox);
          }}
        >
          {this.startingTags}
          {this.userTags}
        </calcite-combobox>
      </calcite-label>
    );
  }

  private _renderTags(tags: string[], isSelected: boolean): VNode[] {
    return tags
      ?.filter((tag: string) => {
        return tag != null && tag !== "";
      })
      ?.map((tag: string) => {
        return (
          <calcite-combobox-item
            key={tag + Math.random()}
            value={tag}
            text-label={tag}
            scale="m"
            selected={isSelected}
          ></calcite-combobox-item>
        );
      });
  }

  /** Find <input/> in combobox and setup blur event */
  private _comboboxHandler(combobox: HTMLCalciteComboboxElement) {
    this._combobox = combobox;

    combobox.addEventListener(
      "calciteComboboxFilterChange",
      this._handleComboboxOnInputChg.bind(this)
    );

    this._recursiveFindGrandChildren(combobox, 0);
  }

  private _maxTries = 10;
  /** Continue calling until grandchildren are found */
  private _recursiveFindGrandChildren(
    combobox: HTMLCalciteComboboxElement,
    tries?: number
  ) {
    if (tries != null && tries > this._maxTries) {
      return;
    }

    this._findGrandChildren(combobox);
    if (this._comboboxGrandChildren == null) {
      setTimeout(() => {
        this._recursiveFindGrandChildren(
          combobox,
          tries != null ? tries + 1 : null
        );
      }, 100);
    } else {
      // grandchildren found - perform initial call
      this._handleComboboxOnInputChg();
    }
  }

  /** Finding combobox grandchildren to get an accurate amount of the chips and their values */
  private _findGrandChildren(combobox: HTMLCalciteComboboxElement) {
    const gridInput: HTMLDivElement = combobox.shadowRoot.querySelector(
      ".grid-input"
    ) as HTMLDivElement;
    this._comboboxGrandChildren = gridInput?.children;
  }

  private _handleComboboxOnInputChg(onChgEvent?: Event) {
    // clear current tags
    this._appTags = [];

    if (this._comboboxGrandChildren == null) {
      this._recursiveFindGrandChildren(this._combobox, 9); // only try once
    }

    // collect tags from the chips
    for (let j = 0; j < this._comboboxGrandChildren.length; j++) {
      const grandChild = this._comboboxGrandChildren[j];
      if (grandChild.className === "chip") {
        applyShadowStyle(CHIP_OVERFLOW_FIX, grandChild as HTMLElement);

        const chipVal = (grandChild as HTMLCalciteChipElement).value;
        if (
          chipVal != null &&
          chipVal !== "" &&
          this._appTags.length <= AMOUNT_OF_TAGS_MAX
        ) {
          const val = this._capStringLength(chipVal);
          this._appTags.push(val);
        }
      } else if (grandChild.className === "input-wrap") {
        const childInput = grandChild.querySelector("input");
        const childInputVal = childInput?.value;
        if (
          childInputVal != null &&
          childInputVal !== "" &&
          this._appTags.length <= AMOUNT_OF_TAGS_MAX
        ) {
          const val = this._capStringLength(childInputVal);
          this._appTags.push(val);
        }
      }
    }

    this._handleTagValidation();
  }

  private _capStringLength(str: string): string {
    if (str.length > TITLE_AND_TAG_STR_MAX_LENGTH) {
      return str.substring(0, TITLE_AND_TAG_STR_MAX_LENGTH);
    } else {
      return str;
    }
  }

  private _handleTagValidation() {
    this._isTagsValid = true;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _renderFolderInput(): VNode {
    const { publishForm } = this.appMessages;
    return (
      <calcite-label>
        {publishForm.folder}
        <calcite-select
          scale="l"
          width="auto"
          afterCreate={(calciteSelect: HTMLCalciteSelectElement) => {
            calciteSelect.addEventListener(
              "calciteSelectChange",
              this._handleFolderChange.bind(this)
            );
          }}
        >
          {this.publishAppVM?.folders?.map((folder: UserFolder) => {
            return (
              <calcite-option
                label={folder.title}
                value={folder.id}
                selected={folder.id === this._folderId}
              >
                {folder.title}
              </calcite-option>
            );
          })}
        </calcite-select>
      </calcite-label>
    );
  }

  private async _handleFolderChange(event: Event) {
    this._folderId = (event.target as any).selectedOption.value;

    if (this._appTitle == null || this._appTitle === "") {
      // if title is not defined yet, then folder change doesn't matter
      return;
    }

    if (
      await this.publishAppVM.doesTitleExist(this._appTitle, this._folderId)
    ) {
      this._showInvalidTitleAlreadyExists(this._appTitle);
    } else {
      this._showValidTitle();
    }
  }

  private _showValidTitle() {
    this.isTitleValid = true;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _showInvalidTitleAlreadyExists(appTitle: string) {
    this.isTitleValid = false;
    this._titleInvalidMsg = `"${appTitle}" ${
      this.appMessages.publishForm.titleValidationExists
    } "${this.publishAppVM.getFolderName(this._folderId)}"`;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _hasForbiddenChars(titleVal: string): boolean {
    const result = FORBIDDEN_TITLE_CHARS.map((forbiddenChar: string) => {
      return titleVal.includes(forbiddenChar);
    }).reduce((acc, curr) => {
      return acc || curr;
    }, false);
    return result;
  }

  private _showInvalidTitleChars() {
    this.isTitleValid = false;
    this._titleInvalidMsg = `${
      this?.appMessages?.publishForm?.appTitleInputRestriction
    }: ${FORBIDDEN_TITLE_CHARS.join(
      ` ${this?.appMessages?.generalUse?.conjunctions?.or} `
    )}`;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _showInvalidEmptyTitle() {
    this.isTitleValid = false;
    this._titleInvalidMsg = this.appMessages.publishForm.titleValidationEmpty;
    this._determineFormValidity();
    this.scheduleRender(); // required here - ui refresh doesn't work without it
  }

  private _destroyPublishOverlay() {
    this.sendFocusOnClose?.();
    if (this?.updateFunction != null) {
      this.updateFunction();
    }
    this.destroy();
    this.scheduleRender();
  }

  private _formatAGOLTags(): string {
    return this._appTags.join(",");
  }

  private _decideDefaultSelectedFolder(): string {
    if (this.mode === "newApp") {
      if (
        this.resourceInfo.resourceType === EResourceType.Webmap ||
        this.resourceInfo.resourceType === EResourceType.Webscene
      ) {
        // WEBMAP OR WEBSCENE
        const resourceItemFolder = (
          this?.resourceInfo?.resourceItem as __esri.PortalItem
        )?.ownerFolder;
        // Make sure that the folder that the resource is in is a folder avaliable to the User
        const folderAvailableToUser: boolean =
          this.publishAppVM?.folders?.findIndex((userFolder) => {
            return userFolder.id === resourceItemFolder;
          }) !== -1;
        return folderAvailableToUser
          ? resourceItemFolder
          : AGOL_USER_ROOT_FOLDER_KEY;
      } else {
        return AGOL_USER_ROOT_FOLDER_KEY;
      }
    } else {
      // "Save Copy" or "from Existing" - Always save to folder original item is in
      return this.existingAppItem.ownerFolder ?? AGOL_USER_ROOT_FOLDER_KEY;
    }
  }
}
